import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  FormHelperText,
  Paper,
  LinearProgress,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Description as FileIcon,
  Image as ImageIcon,
  InsertDriveFile as GenericFileIcon,
  PictureAsPdf as PdfIcon,
  VideoLibrary as VideoIcon,
  AudioFile as AudioIcon,
} from '@mui/icons-material';

/**
 * Component for handling file uploads in form renderer
 * IMPORTANT: Files are now stored locally and only uploaded when the form is submitted
 */
const FileUploadField = forwardRef(({
  label,
  value = [],
  onChange,
  error = false,
  helperText = '',
  required = false,
  multiple = false,
  maxFiles = 10,
  maxSize = 5 * 1024 * 1024, // 5MB default
  accept,
  disabled = false,
  className,
  sx = {},
  formSubmissionId,
  isPublic = false,
  shareToken,
}, ref) => {
  const [files, setFiles] = useState([]);
  const [uploadErrors, setUploadErrors] = useState({});
  const fileInputRef = useRef(null);
  const initialized = useRef(false);

  // Expose method to get file objects to parent component
  useImperativeHandle(ref, () => ({
    getFileObjects: () => {
      return files.map(fileEntry => ({
        ...fileEntry,
        // Return the actual File object for submission
        file: fileEntry.file || null
      }));
    },
    // Additional helper to check if there are pending files
    hasPendingFiles: () => {
      return files.some(file => file.status === 'pending');
    }
  }));

  // Initialize from value only once or when value changes intentionally
  useEffect(() => {
    // Prevent unnecessary re-renders causing an infinite loop
    if (!initialized.current || JSON.stringify(value) !== JSON.stringify(files)) {
      if (value && Array.isArray(value)) {
        setFiles(value);
      } else if (value && !Array.isArray(value)) {
        setFiles([value]);
      } else {
        setFiles([]);
      }
      initialized.current = true;
    }
  }, [value, files]);

  // Handle file selection from input
  const handleFileInputChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate file count
    if ((files.length + selectedFiles.length) > maxFiles) {
      setUploadErrors(prev => ({ 
        ...prev, 
        general: `Maximum of ${maxFiles} files allowed`
      }));
      return;
    }

    // Reset file input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Process files and stage for upload
    await processFiles(selectedFiles);
  };

  // Process and stage selected files for later upload
  const processFiles = async (selectedFiles) => {
    const newFiles = [];
    const newErrors = {};
    
    console.log('Processing selected files:', selectedFiles.length);
    
    // Process each file (but don't upload yet)
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const fileId = `file-${Date.now()}-${i}`;
      
      // Validate file size
      if (file.size > maxSize) {
        newErrors[fileId] = `File size exceeds maximum allowed size (${formatBytes(maxSize)})`;
        continue;
      }
      
      // Create file entry with the actual file object for later upload
      const newFile = {
        id: fileId,
        name: file.name,
        size: file.size,
        type: file.type,
        file: file, // Store the actual file object for upload during form submission
        uploadTime: new Date().toISOString(),
        isPublic: isPublic, // Add flag to indicate if from public form
        status: 'pending' // Mark as pending upload
      };
      
      newFiles.push(newFile);
    }
    
    // Update errors state
    setUploadErrors(newErrors);
    
    // If there are errors, don't continue
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    
    // Update files state and trigger onChange
    const updatedFiles = [...files, ...newFiles];
    setFiles(updatedFiles);
    
    console.log(`Staged ${newFiles.length} files for future upload when form is submitted`);
    
    // Call onChange with the updated files - including the actual File objects
    if (multiple) {
      onChange(updatedFiles);
    } else {
      // For single file, just return the last uploaded file
      onChange(updatedFiles[updatedFiles.length - 1]);
    }
  };

  // Handle removing a file
  const handleRemoveFile = (fileIndex) => {
    const updatedFiles = files.filter((_, index) => index !== fileIndex);
    setFiles(updatedFiles);
    
    // Call onChange with the updated files
    if (multiple) {
      onChange(updatedFiles);
    } else if (updatedFiles.length > 0) {
      onChange(updatedFiles[0]);
    } else {
      onChange(null);
    }
  };

  // Open file dialog
  const handleOpenFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Render drop zone
  const renderDropZone = () => {
    const isUploadDisabled = disabled || (files.length >= maxFiles && multiple);
    
    return (
      <Box
        sx={{
          border: '2px dashed',
          borderColor: error ? 'error.main' : 'divider',
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          backgroundColor: error ? 'error.light' : 'background.paper',
          transition: 'all 0.2s',
          opacity: isUploadDisabled ? 0.6 : 1,
          ...sx
        }}
        className={className}
      >
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
          multiple={multiple}
          accept={accept}
          disabled={isUploadDisabled}
        />
        
        <UploadIcon 
          sx={{ 
            fontSize: 40, 
            color: error ? 'error.main' : 'primary.main', 
            mb: 1 
          }} 
        />
        
        <Typography variant="body1" gutterBottom>
          {label || 'Upload files'}
          {required && ' *'}
        </Typography>
        
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          {multiple 
            ? `Select files to upload when form is submitted (${files.length}/${maxFiles} files, max ${formatBytes(maxSize)} each)` 
            : `Select a file to upload when form is submitted (max ${formatBytes(maxSize)})`}
        </Typography>
        
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={handleOpenFileDialog}
          disabled={isUploadDisabled}
          sx={{ mt: 1 }}
        >
          Select File{multiple ? 's' : ''}
        </Button>
        
        {error && helperText && (
          <FormHelperText error>{helperText}</FormHelperText>
        )}
        
        {uploadErrors.general && (
          <FormHelperText error>{uploadErrors.general}</FormHelperText>
        )}
      </Box>
    );
  };

  // Get icon for file type
  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) return <ImageIcon />;
    if (fileType?.startsWith('video/')) return <VideoIcon />;
    if (fileType?.startsWith('audio/')) return <AudioIcon />;
    if (fileType === 'application/pdf') return <PdfIcon />;
    if (fileType?.includes('document') || fileType?.includes('text/')) return <FileIcon />;
    return <GenericFileIcon />;
  };

  // Format bytes to human-readable format
  const formatBytes = (bytes, decimals = 1) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  // Render file preview
  const renderFilePreview = (file, index) => {
    const uploadError = uploadErrors[file.id];
    
    return (
      <ListItem
        key={file.id || index}
        sx={{
          border: '1px solid',
          borderColor: uploadError ? 'error.main' : 'divider',
          borderRadius: 1,
          mb: 1,
          p: 1
        }}
      >
        <Grid container alignItems="center" spacing={1}>
          {/* File icon */}
          <Grid item xs={1}>
            {getFileIcon(file.type)}
          </Grid>
          
          {/* File info */}
          <Grid item xs={9}>
            <ListItemText
              primary={file.name}
              secondary={`${formatBytes(file.size)} • Will be uploaded on form submission`}
              secondaryTypographyProps={{
                variant: 'caption',
                color: file.status === 'pending' ? 'info.main' : 'text.secondary'
              }}
            />
            
            {uploadError && (
              <Typography variant="caption" color="error">
                {uploadError}
              </Typography>
            )}
          </Grid>
          
          {/* Remove button */}
          {!disabled && (
            <Grid item xs={2}>
              <ListItemSecondaryAction>
                <IconButton 
                  edge="end" 
                  onClick={() => handleRemoveFile(index)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </Grid>
          )}
        </Grid>
      </ListItem>
    );
  };

  return (
    <Box>
      {/* Label */}
      <Typography 
        variant="subtitle2" 
        color={error ? 'error' : 'text.primary'}
        gutterBottom
      >
        {label}{required ? ' *' : ''}
      </Typography>
      
      {/* Upload zone */}
      {renderDropZone()}
      
      {/* File list */}
      {files.length > 0 && (
        <List sx={{ mt: 2 }}>
          {files.map((file, index) => renderFilePreview(file, index))}
        </List>
      )}
      
      {/* Additional helper text about form submission */}
      {files.length > 0 && (
        <Typography variant="body2" color="info.main" sx={{ mt: 1, fontStyle: 'italic' }}>
          {files.length} file(s) will be uploaded when the form is submitted
        </Typography>
      )}
    </Box>
  );
});

export default FileUploadField;